import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Home, FileText, Bell } from 'lucide-react'
import OAuthLogin from '../components/OAuthLogin'

export default function LoginPage() {
  const [error, setError] = useState('')
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo') || '/customer'
  const sessionExpired = searchParams.get('expired') === 'true'

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 flex">
      {/* Left panel — branding & value props */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 text-white relative overflow-hidden">
        {/* Decorative background circles */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/5 rounded-full" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-white/5 rounded-full" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-white/5 rounded-full" />

        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center text-white/70 hover:text-white transition-colors mb-8 text-sm">
            <ArrowLeft size={16} className="mr-1.5" />
            Back to Home
          </Link>
          <div className="mb-2">
            <img src="/LoanLoopLogo.jpg" alt="LoanLoop" className="h-14 w-auto rounded-lg shadow-lg mb-4" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Your Home Loan, Simplified</h1>
          <p className="text-lg text-white/70 max-w-md">
            Sign in to track your application, compare rates, and connect with expert brokers.
          </p>
        </div>

        <div className="relative z-10 space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
              <Home size={20} />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Track Your Application</h3>
              <p className="text-sm text-white/60">Follow every step from pre-approval to settlement in real time.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
              <FileText size={20} />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Digital Documents</h3>
              <p className="text-sm text-white/60">Upload and manage your documents securely — no more paperwork.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
              <Bell size={20} />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Stay Informed</h3>
              <p className="text-sm text-white/60">Get notified the moment your application moves to the next stage.</p>
            </div>
          </div>
        </div>

        <p className="relative z-10 text-xs text-white/40">
          &copy; {new Date().getFullYear()} LoanLoop Pty Ltd (ACN 696 406 112). All rights reserved.
        </p>
      </div>

      {/* Right panel — sign-in form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md">
          {/* Mobile back link */}
          <Link to="/" className="lg:hidden inline-flex items-center text-white/70 hover:text-white transition-colors mb-8 text-sm">
            <ArrowLeft size={16} className="mr-1.5" />
            Back to Home
          </Link>

          <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10">
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-6">
              <img src="/LoanLoopLogo.jpg" alt="LoanLoop" className="h-12 w-auto mx-auto mb-2" />
              <span className="text-2xl font-bold text-primary-600">LoanLoop</span>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Sign in to LoanLoop</h2>
              <p className="text-gray-500 mt-1">Customers and approved brokers: choose your sign-in method below.</p>
            </div>

            {sessionExpired && (
              <div className="mb-5 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                Your session has expired. Please sign in again.
              </div>
            )}

            {error && (
              <div className="mb-5 p-3 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">{error}</div>
            )}

            <OAuthLogin onError={setError} returnTo={returnTo} />

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">or</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            <Link
              to="/broker"
              className="w-full flex items-center justify-center gap-2 text-primary-600 border border-primary-200 bg-primary-50 py-3 rounded-xl hover:bg-primary-100 transition-colors font-medium text-sm"
            >
              Broker Portal Login
            </Link>

            <p className="text-center text-xs text-gray-400 mt-6">
              Protected by enterprise-grade encryption
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
